import React, { useState } from 'react';
import { MoreHorizontal, Eye, Edit, Key, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatToCurrency } from '../../utils/common';
import { UserAvatar, ConfirmModal, ActionMenu, ActionMenuItem, ActionMenuDivider } from '../ui';
import { useBranch } from '../../contexts/BranchContext';
import DataTable from '../Common/DataTable';

const StudentList = ({ 
  students = [], 
  total = 0, 
  page = 1, 
  setPage = () => {}, 
  selected = [], 
  setSelected, 
  pageSize = 10, 
  onDelete, 
  resetStudentPassword, 
  resendWelcomeEmail, 
  parentPage
}) => {
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isMultiBranch, branches } = useBranch();

  // Define table columns
  const columns = [
    {
      key: 'student',
      label: 'Student',
      width: '25%',
      render: (student) => (
        <div className="d-flex align-items-center gap-3">
          <UserAvatar name={student.name} size="sm" />
          <div>
            <a
              href={`/students/${student._id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/students/${student._id}`);
              }}
              className="fw-semibold text-decoration-none clickable"
            >
              {student.name}
              {student.status === "active" && student.accountCreated && (
                <span className="status-indicator active">●</span>
              )}
              {student.status === "inactive" && (
                <span className="status-indicator inactive" title='Inactive'>●</span>
              )}
            </a>
            <div className="text-muted text-sm">{student.mobile || "-"}</div>
            <div className="text-muted text-sm">{student.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'course',
      label: 'Course',
      width: '20%',
      render: (student) => {
        const additional = (student.courseAssignments || []).filter(a => !a.isPrimary && a.status === 'active');
        return (
          <div>
            <div className="fw-medium">{student.course?.name || "-"}</div>
            <div className="text-muted text-sm">{student.batch?.name || "-"}</div>
            {additional.length > 0 && (
              <div className="mt-1">
                {additional.map((a, i) => (
                  <span
                    key={a._id || i}
                    className="badge bg-light text-dark border me-1 mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 500 }}
                  >
                    {a.courseId?.name || 'Course'}
                    {a.batchId?.name ? ` · ${a.batchId.name}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    ...(isMultiBranch && branches.length > 1 ? [{
      key: 'branch',
      label: 'Branch',
      width: '12%',
      render: (student) => (
        <span className="text-muted text-sm">{student.batch?.branchId?.name || '—'}</span>
      )
    }] : []),
    ...(parentPage === "students" ? [{
      key: 'fees',
      label: 'Fees',
      width: '15%',
      render: (student) => (
        <div>
          <div className="fw-medium">{formatToCurrency(student.totalFees || 0)}</div>
          <div className="text-muted text-sm">
            Pending: {formatToCurrency(student.pendingFees || 0)}
          </div>
        </div>
      )
    }] : []),
    {
      key: 'aiAttempts',
      label: 'AI Attempts',
      width: '12%',
      render: (student) => student.totalAIAttempts || "-"
    },
    {
      key: 'assignments',
      label: 'Assignment Sessions',
      width: '15%',
      render: (student) => student.totalAssignmentSessions || "-"
    },
    {
      key: 'joinedDate',
      label: 'Joined Date',
      width: '12%',
      render: (student) => student.createdAt ? new Date(student.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"
    },
    {
      key: 'actions',
      label: '',
      width: '50px',
      render: (student) => (
        <div onClick={(e) => e.stopPropagation()}>
          <ActionMenu trigger={<MoreHorizontal size={16} />}>
            <ActionMenuItem onClick={() => navigate(`/students/${student._id}`)}>
              <Eye size={14} /> View Details
            </ActionMenuItem>
            <ActionMenuItem onClick={() => navigate(`/students/edit/${student._id}`)}>
              <Edit size={14} /> Edit
            </ActionMenuItem>
            {student.accountCreated && resetStudentPassword && (
              <ActionMenuItem onClick={() => resetStudentPassword(student)}>
                <Key size={14} /> Reset password
              </ActionMenuItem>
            )}
            {!student.accountCreated && resendWelcomeEmail && (
              <ActionMenuItem onClick={() => resendWelcomeEmail(student)}>
                <Mail size={14} /> Resend Welcome Email
              </ActionMenuItem>
            )}
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setDeleteTarget(student)}>
              <Trash2 size={14} /> Delete
            </ActionMenuItem>
          </ActionMenu>
        </div>
      )
    }
  ];

  return (
    <>
    <DataTable
      data={students}
      columns={columns}
      selectable={true}
      selectedRows={selected}
      onSelectionChange={setSelected}
      hoverable={true}
      rowClassName={(row) => row.status === 'inactive' ? 'student-row--inactive' : ''}
      pagination={{
        page,
        pageSize,
        total,
        onPageChange: setPage
      }}
      emptyMessage="No students found"
    />

    <ConfirmModal
      show={!!deleteTarget}
      onHide={() => setDeleteTarget(null)}
      onConfirm={() => { onDelete(deleteTarget._id); setDeleteTarget(null); }}
      title="Delete Student"
      message={`Are you sure you want to delete ${deleteTarget?.name}?`}
      confirmText="Delete"
      variant="danger"
    />
    </>
  );
};

export default StudentList;
